import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { useWindowStore, useAppStore, useConfigStore } from '../../stores';

import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import FadingContainer from '../FadingContainer/FadingContainer';
import logoUrl from './blue-fingerprint.png';
import FinishCollectionNav from './FinishCollectionNav';

import './FinishCollection.scss';

const FinishCollection = ( {
  id,
  finishCollection,
  className,
} ) => {
  const [ spaces, setSpaces ] = useState( [] );
  const [ palettes, setPalettes ] = useState( [] );
  const [ backgroundImage, setBackgroundImage ] = useState( null );
  const [ activeImage, setActiveImage ] = useState( null );
  const [ activeSpace, setActiveSpace ] = useState( null );
  const [ activePalette, setActivePalette ] = useState( null );

  const isMobile = useWindowStore((state) => state.isMobile);
  const settings = useAppStore((state) => state.settings);
  const config = useConfigStore((state) => state.config);
  const { blueFingerprintUrl = 'https://app3.bluefingerprint.com/'} = config || {};
  const { platform } = settings || {};

  // Loop through finish options to build space button data. Data is already
  // weighted correctly.
  const rawSpaces = finishCollection?.spaces;
  const defaultSpaceId = finishCollection?.defaultSpaceId;
  useEffect( () => {
    const spaceOptions = [];
    rawSpaces?.forEach( ( space ) => {
      const spaceOption = {
        id: space.spaceId,
        text: space.spaceName,
        isDefault: defaultSpaceId === space.spaceId,
      };
      spaceOptions.push( spaceOption );
    } );
    setSpaces( spaceOptions );
  }, [ rawSpaces, defaultSpaceId ] );

  // Set background image of palette container. We're showing the image of
  // selected space/palette combo by updating the a background image in a persistent div
  // rather than hiding/showing divs like a tradtional carousel.
  const activeBackgroundImage = finishCollection?.images?.length
    ? finishCollection.images[0]?.imageUrl
    : null;
  useEffect( () => {
    setBackgroundImage( activeBackgroundImage );
  }, [ activeBackgroundImage ] );

  // Loop through space palettes to build palette button data. Data is already
  // weighted correctly.
  useEffect( () => {
    if ( activeSpace ) {
      const paletteOptions = [];
      const activeSpaceData = rawSpaces?.find(
        ( { spaceId } ) => spaceId === activeSpace,
      );
      activeSpaceData?.palettes.paletteOptions.forEach( ( palette ) => {
        const paletteOption = {
          id: palette.paletteId,
          text: palette.paletteName,
          isDefault:
            activeSpaceData.palettes.defaultPaletteId === palette.paletteId,
        };
        // Set initial palette, i.e. Palette A, Palette One, etc. When a space is
        // initially loaded or chosen, we want to show it in the default palette for that
        // space, which is a taxonomy field info that comes over from Drupal.
        if ( paletteOption.isDefault ) {
          setActivePalette( paletteOption.id );
        }
        paletteOptions.push( paletteOption );
      } );
      setPalettes( paletteOptions );
    }
  }, [ activeSpace, rawSpaces ] );

  // Set active image.
  const rawImages = finishCollection?.images;
  useEffect( () => {
    if ( activeSpace && activePalette && rawImages ) {
      const filter = {
        spaceId: activeSpace,
        paletteId: activePalette,
      };

      const image = rawImages.filter( ( item ) => {
        // eslint-disable-next-line no-restricted-syntax
        for ( const key in filter ) {
          if ( item[key] === undefined || item[key] !== filter[key] ) {
            return false;
          }
        }
        return true;
      } );

      const newActiveImage = image && image[0] ? image[0].imageUrl : null;
      setActiveImage( newActiveImage );
    }
  }, [
    activeSpace, activePalette, rawImages,
  ] );

  const finishCollectionNav = (
    <FinishCollectionNav
      spaces={spaces}
      palettes={palettes}
      setActiveSpace={setActiveSpace}
      setActivePalette={setActivePalette}
    />
  );

  return finishCollection && Object.keys( finishCollection ).length ? (
    <FadingContainer>
      <div id={id} className={className || ''}>

        { isMobile && finishCollectionNav }

        <div>
          <div className="finishCollection">

            <div className="finishCollection__background">
              <div
                className="finishCollection__backgroundBlur"
                style={{ backgroundImage: `url(${backgroundImage})` }}
              />
            </div>

            <div
              className="finishCollection__image"
              style={{ backgroundImage: `url(${activeImage})` }}
            >
              { !isMobile && finishCollectionNav }

            </div>

            { !activeImage && <LoadingSpinner /> }

          </div>
        </div>

        { ( platform === 'yt' || platform === 'kiosk' ) && (
          <div className="finishCollection__blueFingerprint">
            <a
              className="finishCollection__blueFingerprintLink"
              target="_blank"
              rel="noreferrer noopener"
              href={blueFingerprintUrl}
            >
              <img
                src={logoUrl}
                alt="BlueFingerprint"
                className="finishCollection__blueFingerprintLogo"
              />
            </a>
          </div>
        )}
      </div>
    </FadingContainer>
  ) : null;
};

FinishCollection.propTypes = {
  id: PropTypes.string,
  finishCollection: PropTypes.shape( {} ),
  className: PropTypes.string,
};

FinishCollection.defaultProps = {
  id: '',
  finishCollection: {},
  className: '',
};

export default FinishCollection;
